import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { ArrowLeft, Check, Loader2, Send, Trash2, User as UserIcon, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useTelegramAutoLogin } from '@/hooks/useTelegramAutoLogin';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { useT } from '@/contexts/I18nContext';
import { extractApiError } from '@/utils/apiError';
import { cn } from '@/lib/utils';
import {
  deleteVisit,
  getVisit,
  getVisitSendPreview,
  patchVisit,
  sendVisit,
  setVisitAnswer,
  submitVisit,
  type VisitDetailDto,
  type VisitParameterAnswerDto,
} from '@/api/visits';
import { BiathlonDots, DeltaChip, formatPct, formatPoints, formatWeight, pctColorClass, TotalScoreBadge } from '@/components/visits/scoreDisplay';
import { ParameterCommentButton } from '@/components/visits/ParameterCommentButton';

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(local: string): string {
  return new Date(local).toISOString();
}

function applyOptimisticAnswer(visit: VisitDetailDto, parameterId: number, value: boolean | null): VisitDetailDto {
  return {
    ...visit,
    groups: visit.groups.map(g => ({
      ...g,
      parameters: g.parameters.map(p => (p.id === parameterId ? { ...p, value } : p)),
    })),
  };
}

// Keyed by visitId so navigating from one visit straight to another (e.g.
// list -> visit A -> back -> visit B) fully remounts the page instead of
// reusing state across visits — the general-comment Textarea below is
// uncontrolled (defaultValue + onBlur-commit, matching VisitsSettings'
// "always-active field" pattern) and would otherwise show visit A's stale
// draft text after the route param changes underneath it.
export default function VisitDetailPage() {
  const { visitId } = useParams<{ visitId: string }>();
  return <VisitDetailPageInner key={visitId} visitId={visitId} />;
}

function VisitDetailPageInner({ visitId }: { visitId: string | undefined }) {
  useTelegramAutoLogin();
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  const navigate = useNavigate();
  const id = Number(visitId);

  const { data: visit, error, isLoading, mutate } = useSWR<VisitDetailDto>(
    Number.isFinite(id) ? ['visits:detail', id] : null,
    async () => (await getVisit(id)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  usePageTitle(t('visits.liveTitle'), t('visits.liveSubtitle'));

  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [commentDraft, setCommentDraft] = useState<string | null>(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendState, setSendState] = useState<'idle' | 'sent'>('idle');

  if (!Number.isFinite(id)) return null;

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-4xl px-4 py-6 text-sm text-muted-foreground xl:max-w-none xl:px-8">
        {t('common.loading')}
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-4 px-4 py-6 xl:max-w-none xl:px-8">
        <BackLink navigate={navigate} t={t} />
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            {t('visits.notFound')}
          </CardContent>
        </Card>
      </div>
    );
  }

  const canEdit = visit.can_edit;
  const canDelete = visit.can_delete;
  const time = new Date(visit.visit_time).toLocaleString(locale, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  // Optimistic: flip the button state immediately (not awaited by the
  // caller) instead of waiting on the round-trip — Alexey 2026-08-25:
  // "да/нет должен оптимистичный ux быть, он тормозит с переключением".
  // Percentages/total stay whatever they were until the real response
  // lands a moment later — only the answered value itself needs to feel
  // instant, and recomputing scores client-side would duplicate
  // scoring.py's logic for no real benefit. On error, revert to the
  // pre-click visit and surface the message.
  const handleAnswer = (parameterId: number, value: boolean | null) => {
    setSaveError(null);
    const previous = visit;
    mutate(applyOptimisticAnswer(visit, parameterId, value), { revalidate: false });
    setVisitAnswer(id, { parameter_id: parameterId, value })
      .then(resp => mutate(resp.data, { revalidate: false }))
      .catch(e => {
        mutate(previous, { revalidate: false });
        setSaveError(extractApiError(e, t('error.general')));
      });
  };

  const handleParamComment = async (parameterId: number, currentValue: boolean | null, text: string) => {
    // A comment needs an answer row to hang off of — default to "no" if the
    // parameter hasn't been answered yet so a comment can be left first.
    const resp = await setVisitAnswer(id, {
      parameter_id: parameterId,
      value: currentValue ?? false,
      comment: text,
    });
    await mutate(resp.data, { revalidate: false });
  };

  const handleCommentBlur = async () => {
    if (commentDraft === null || commentDraft === visit.comment) {
      setCommentDraft(null);
      return;
    }
    const next = commentDraft;
    setCommentDraft(null);
    try {
      const resp = await patchVisit(id, { comment: next });
      await mutate(resp.data, { revalidate: false });
    } catch (e) {
      setSaveError(extractApiError(e, t('error.general')));
    }
  };

  const handleVisitTimeChange = async (local: string) => {
    try {
      const resp = await patchVisit(id, { visit_time: fromDatetimeLocal(local) });
      await mutate(resp.data, { revalidate: false });
    } catch (e) {
      setSaveError(extractApiError(e, t('error.general')));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSaveError(null);
    try {
      const resp = await submitVisit(id);
      await mutate(resp.data, { revalidate: false });
    } catch (e) {
      setSaveError(extractApiError(e, t('error.general')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const confirmMsg = visit.is_draft ? t('visits.deleteDraftConfirm') : t('visits.deleteVisitConfirm');
    if (!window.confirm(confirmMsg)) return;
    setSubmitting(true);
    setSaveError(null);
    try {
      await deleteVisit(id);
      navigate('/usage/visits');
    } catch (e) {
      setSaveError(extractApiError(e, t('error.general')));
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 px-4 py-6 xl:max-w-none xl:px-8">
      <h1 className="sr-only">{t('visits.liveTitle')}</h1>
      <BackLink navigate={navigate} t={t} />

      {/* Header: author, status, editable visit time, running total */}
      <Card>
        <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 space-y-1.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {visit.is_draft && <Badge variant="default">{t('visits.draftBadge')}</Badge>}
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <UserIcon className="size-3.5" />
                {visit.author.name}
              </span>
              {!visit.is_draft && !canEdit && (
                <Badge variant="muted">{t('visits.readOnly')}</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('visits.visitTime')}:</span>
              {canEdit ? (
                <Input
                  type="datetime-local"
                  defaultValue={toDatetimeLocal(visit.visit_time)}
                  onBlur={e => e.target.value && handleVisitTimeChange(e.target.value)}
                  className="h-7 w-[13.5rem] text-xs"
                />
              ) : (
                <span className="text-xs font-medium">{time}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-xs text-muted-foreground">{t('visits.total')}</div>
              <div className="flex items-baseline gap-1.5">
                <TotalScoreBadge total={visit.total} className="text-base" />
                {visit.previous_total !== null && (
                  <span className="inline-flex items-baseline gap-1 text-xs text-muted-foreground">
                    {formatPct(visit.previous_total)}
                    <DeltaChip delta={visit.total_delta} />
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {saveError && (
        <p className="text-xs text-destructive">{saveError}</p>
      )}

      {/* Parameter groups grid — one card per group, dense rows so everything
          tries to fit in one screen (Alexey: "стараться уместить в одно окно"). */}
      <div className="grid gap-3 sm:grid-cols-2">
        {visit.groups.map(group => (
          <Card key={group.id} className="overflow-hidden">
            <CardContent className="space-y-1 py-3">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{group.name}</span>
                <span className="inline-flex items-baseline gap-1.5">
                  <span className={cn('text-sm font-semibold tabular-nums', pctColorClass(group.percentage, group.formula_coefficient < 0))}>
                    {formatPct(group.percentage)}
                  </span>
                  {group.previous_percentage !== null && (
                    <span className="inline-flex items-baseline gap-1 text-xs text-muted-foreground tabular-nums">
                      {formatPct(group.previous_percentage)}
                      <DeltaChip delta={group.delta} />
                    </span>
                  )}
                </span>
              </div>
              <div className="divide-y divide-border/60">
                {group.parameters.map(parameter => (
                  <ParameterRow
                    key={parameter.id}
                    parameter={parameter}
                    groupInverted={group.formula_coefficient < 0}
                    canEdit={canEdit}
                    onAnswer={value => handleAnswer(parameter.id, value)}
                    onComment={text => handleParamComment(parameter.id, parameter.value, text)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* General spot comment — always-active text field, per spec. */}
      <Card>
        <CardContent className="space-y-1.5 py-3">
          <span className="text-xs font-medium text-muted-foreground">{t('visits.generalComment')}</span>
          <Textarea
            defaultValue={visit.comment}
            onChange={e => setCommentDraft(e.target.value)}
            onBlur={handleCommentBlur}
            placeholder={t('visits.generalCommentPlaceholder')}
            disabled={!canEdit}
            className="min-h-[70px] text-sm"
          />
        </CardContent>
      </Card>

      {(canDelete || (canEdit && visit.is_draft) || !visit.is_draft) && (
        <div className="flex justify-between">
          {canDelete ? (
            <Button
              variant="ghost"
              onClick={handleDelete}
              disabled={submitting}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="size-4" />
              {visit.is_draft ? t('visits.deleteDraft') : t('visits.deleteVisit')}
            </Button>
          ) : <span />}
          {canEdit && visit.is_draft && (
            <Button onClick={handleSubmit} disabled={submitting} className="gap-1.5">
              <Check className="size-4" />
              {t('visits.submit')}
            </Button>
          )}
          {!visit.is_draft && (
            <Button
              variant={sendState === 'sent' ? 'outline' : 'default'}
              onClick={() => setSendModalOpen(true)}
              className="gap-1.5"
            >
              <Send className="size-4" />
              {sendState === 'sent' ? t('visits.sendToTeamSent') : t('visits.sendToTeam')}
            </Button>
          )}
        </div>
      )}

      {!visit.is_draft && (
        <SendToTeamDialog
          visitId={id}
          open={sendModalOpen}
          onClose={() => setSendModalOpen(false)}
          onSent={() => setSendState('sent')}
        />
      )}
    </div>
  );
}

// "Отправить команде" confirm modal (Alexey 2026-08-27): shows the exact
// image the send would post before actually posting it. The preview call
// re-renders on the backend rather than reusing a cached image, so it's
// guaranteed current with the visit's latest answers/comments.
function SendToTeamDialog({
  visitId, open, onClose, onSent,
}: {
  visitId: number;
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}) {
  const { t } = useT();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setError(null);
    setLoadingPreview(true);
    getVisitSendPreview(visitId)
      .then(resp => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(resp.data);
        setPreviewUrl(objectUrl);
      })
      .catch(e => !cancelled && setError(extractApiError(e, t('error.general'))))
      .finally(() => !cancelled && setLoadingPreview(false));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPreviewUrl(null);
    };
  }, [open, visitId, t]);

  async function handleConfirm() {
    setSending(true);
    setError(null);
    try {
      await sendVisit(visitId);
      onSent();
      onClose();
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && !sending && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('visits.sendToTeam')}</DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[60vh] items-center justify-center overflow-auto rounded-md border border-border bg-muted/20">
          {loadingPreview && <Loader2 className="m-8 size-6 animate-spin text-muted-foreground" />}
          {!loadingPreview && previewUrl && (
            <img src={previewUrl} alt="" className="w-full object-contain" />
          )}
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={sending || loadingPreview || !previewUrl} className="gap-1.5">
            {sending && <Loader2 className="size-3.5 animate-spin" />}
            {t('visits.sendConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BackLink({ navigate, t }: { navigate: ReturnType<typeof useNavigate>; t: (k: string) => string }) {
  return (
    <button
      type="button"
      onClick={() => navigate('/usage/visits')}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
      {t('visits.backToList')}
    </button>
  );
}

function ParameterRow({
  parameter,
  groupInverted,
  canEdit,
  onAnswer,
  onComment,
}: {
  parameter: VisitParameterAnswerDto;
  groupInverted: boolean;
  canEdit: boolean;
  onAnswer: (value: boolean | null) => void;
  onComment: (text: string) => void | Promise<void>;
}) {
  const { t } = useT();
  // The biathlon replaces the yes/no control on the VIEW page for a changed
  // parameter — Alexey: "на странице просмотра визита ... вместо чекбокса".
  const showBiathlon = !canEdit && parameter.changed && parameter.history.length > 0;
  // Two independent signs compose (Alexey 2026-08-26 — "минус на минус даст
  // плюс"): the group's own formula_coefficient (a whole SECTION can score
  // backwards, e.g. "Вредные привычки") AND the parameter's own weight_yes
  // (a single factor can be the exception inside an otherwise-normal group).
  // XOR them — double-negative cancels back to normal coloring.
  const inverted = groupInverted !== (parameter.weight_yes < 0);
  const yesColorClass = inverted ? 'bg-destructive text-white' : 'bg-success text-white';
  const noColorClass = inverted ? 'bg-success text-white' : 'bg-destructive text-white';

  // View mode shows the achieved FACT for this answer, not the weight
  // potential (Alexey 2026-08-27: "на просмотре должно показываться уже
  // факт, сколько дали за тот или иной фактор") — edit mode keeps the
  // weight hint since the checker needs to see the stakes before answering.
  const achieved = parameter.value === true ? parameter.weight_yes : parameter.value === false ? parameter.weight_no : null;
  const achievedGood = parameter.value === true ? !inverted : inverted;
  const achievedColorClass = achieved === 0
    ? 'text-muted-foreground'
    : achievedGood ? 'text-success' : 'text-destructive';

  return (
    <div className="group flex items-start justify-between gap-2 py-1.5">
      <span className="min-w-0 flex-1 whitespace-normal break-words text-sm">
        {parameter.name}
        {canEdit ? (
          <span className="ml-1.5 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
            ({formatWeight(parameter.weight_yes)}/{formatWeight(parameter.weight_no)})
          </span>
        ) : achieved !== null && (
          <span className={cn('ml-1.5 whitespace-nowrap text-[11px] font-medium tabular-nums', achievedColorClass)}>
            ({formatPoints(achieved)})
          </span>
        )}
      </span>
      <div className="flex shrink-0 items-center gap-1.5">
        {/* Comment button comes first — it's hover-reveal (or hidden until
            answered) but still reserves its width, so it must sit to the
            LEFT of the yes/no control. Otherwise the control (the thing
            every row actually has) ends up offset from the card's right
            edge instead of flush with it, reading as a crooked column when
            not hovering (Alexey 2026-08-25: "кружки съехали налево ...
            выглядит как кривая таблица"). */}
        <ParameterCommentButton
          comment={parameter.comment}
          onSave={onComment}
          readOnly={!canEdit}
        />
        {showBiathlon ? (
          <BiathlonDots history={parameter.history} inverted={inverted} />
        ) : canEdit ? (
          <div className="flex overflow-hidden rounded-md border border-border">
            {/* Clicking the already-active choice again clears it back to
                unanswered (null) — Alexey 2026-08-25: "если ошибочно нажал
                да/нет во время визита, снять уже нельзя". */}
            <button
              type="button"
              onClick={() => onAnswer(parameter.value === true ? null : true)}
              className={cn(
                'px-2 py-1 text-xs font-medium transition-colors',
                parameter.value === true ? yesColorClass : 'bg-background text-muted-foreground hover:bg-muted',
              )}
            >
              {t('visits.yes')}
            </button>
            <button
              type="button"
              onClick={() => onAnswer(parameter.value === false ? null : false)}
              className={cn(
                'border-l border-border px-2 py-1 text-xs font-medium transition-colors',
                parameter.value === false ? noColorClass : 'bg-background text-muted-foreground hover:bg-muted',
              )}
            >
              {t('visits.no')}
            </button>
          </div>
        ) : (
          <span
            className={cn(
              'inline-flex size-5 items-center justify-center rounded-full',
              parameter.value === true && yesColorClass,
              parameter.value === false && noColorClass,
              parameter.value === null && 'border border-border text-muted-foreground',
            )}
          >
            {parameter.value === true && <Check className="size-3" />}
            {parameter.value === false && <X className="size-3" />}
          </span>
        )}
      </div>
    </div>
  );
}
